/**
 * Locale-aware currency formatting via Intl.NumberFormat.
 * en-US → "$59.99", es-ES → "59,99 US$".
 */

const LOCALE_BCP47: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
};

function toBcp47(locale: string): string {
  return LOCALE_BCP47[locale] ?? LOCALE_BCP47.en;
}

/**
 * Whether a code can safely be handed to `Intl.NumberFormat`.
 *
 * Callers that render a PRICE should gate on this rather than relying on the fallback
 * inside `formatCurrency`. Falling back to USD produces a plausible-looking number in
 * the wrong currency, which is worse than showing no price at all — the customer is
 * charged in whatever Stripe actually returned, not in what the label claimed.
 */
export function isSupportedCurrency(
  currency: string | undefined | null,
): currency is string {
  return typeof currency === "string" && /^[A-Za-z]{3}$/.test(currency);
}

export function formatCurrency(
  amount: number,
  locale: string,
  currency = "USD",
): string {
  if (typeof amount !== "number" || isNaN(amount)) return "";
  // `Intl.NumberFormat` throws RangeError on anything that is not a well-formed
  // ISO-4217 code — including the empty string, which a malformed product
  // payload can produce. Prices render on the paywall, so an unguarded throw
  // takes down the one screen a paying customer needs. This is a LAST-RESORT
  // guard: price surfaces gate on `isSupportedCurrency` first so they can hide
  // the price instead of mislabelling it.
  const code = isSupportedCurrency(currency) ? currency.toUpperCase() : "USD";
  return new Intl.NumberFormat(toBcp47(locale), {
    style: "currency",
    currency: code,
  }).format(amount);
}

export type Period = "monthly" | "annual" | "lifetime";

export function formatPricePerPeriod(
  amount: number,
  period: Period,
  locale: string,
  currency = "USD",
): string {
  const price = formatCurrency(amount, locale, currency);
  // Period suffix uses i18n keys at the call site; this helper just returns
  // the price. Use formatCurrency() directly when the period suffix is built
  // separately with `t("paywall.monthly")` etc.
  return price;
}
